import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, StatCard, Money, Spinner } from "@/app/ui";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { AlertTriangle } from "lucide-react";

const COLORS = ["#2563EB", "#0F172A", "#059669", "#D97706", "#7C3AED", "#DC2626", "#0284C7"];

function ReportsInner() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const { data: sum, loading } = useFetch("/reports/summary");
  const { data: vat } = useFetch("/reports/vat");
  const { data: aged } = useFetch("/reports/aged-debtors");

  if (loading || !sum) return <Spinner />;

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader title="Reports & Analytics" description="A plain-language view of how the business is performing." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={<Money value={sum.revenue} symbol={sym} />} tone="green" sub="Money received" />
        <StatCard label="Costs" value={<Money value={sum.costs} symbol={sym} />} tone="amber" sub="Expenses" />
        <StatCard label="Profit" value={<Money value={sum.profit} symbol={sym} />} tone={sum.profit >= 0 ? "green" : "red"} sub="Revenue − costs" />
        <StatCard label="Quote win rate" value={`${sum.quote_win_rate}%`} tone="blue" sub="Accepted quotes" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="amden-card p-6">
          <h3 className="font-semibold text-navy mb-4">Expenses by category</h3>
          {sum.expenses_by_category?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={sum.expenses_by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={(e) => e.category}>
                  {sum.expenses_by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${sym}${v}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm py-16 text-center">No expenses recorded.</p>}
        </div>

        <div className="amden-card p-6">
          <h3 className="font-semibold text-navy mb-4">VAT summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-600">VAT on sales</span><Money value={vat?.vat_on_sales} symbol={sym} /></div>
            <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-600">VAT on purchases</span><Money value={vat?.vat_on_purchases} symbol={sym} /></div>
            <div className="flex justify-between py-2 font-semibold text-navy"><span>Net VAT due (est.)</span><Money value={vat?.net_vat_due} symbol={sym} /></div>
          </div>
          <div className="mt-4 flex gap-2 items-start rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" /><p>{vat?.disclaimer}</p>
          </div>
        </div>
      </div>

      <div className="amden-card p-6">
        <h3 className="font-semibold text-navy mb-4">Aged debtors (who owes you)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {aged && [["Current", aged.buckets.current], ["1–30 days", aged.buckets.d1_30], ["31–60 days", aged.buckets.d31_60], ["60+ days", aged.buckets.d60_plus]].map(([l, v]) => (
            <div key={l} className="rounded-lg bg-slate-50 border border-slate-200 p-3"><p className="text-xs text-slate-500">{l}</p><Money value={v} symbol={sym} className="text-lg font-semibold text-navy" /></div>
          ))}
        </div>
        {aged?.detail?.length ? (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-left"><tr><th className="py-2">Invoice</th><th className="py-2">Customer</th><th className="py-2 text-right">Days overdue</th><th className="py-2 text-right">Balance</th></tr></thead>
            <tbody>{aged.detail.map((d) => (
              <tr key={d.number} className="border-t border-slate-100"><td className="py-2 font-mono text-navy">{d.number}</td><td className="py-2 text-slate-600">{d.customer}</td><td className="py-2 text-right font-mono">{d.days_overdue}</td><td className="py-2 text-right"><Money value={d.balance} symbol={sym} /></td></tr>
            ))}</tbody>
          </table>
        ) : <p className="text-slate-400 text-sm">Nothing outstanding. 🎉</p>}
      </div>
    </div>
  );
}

export default function Reports() { return <PlanGate feature="reports"><ReportsInner /></PlanGate>; }
